import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { UserResponse } from '@careforu-tailody/tailody-core-api';

/**
 * Token storage keys - unified across the application
 */
const TOKEN_KEYS = {
    accessToken: 'token',
    refreshToken: 'refreshToken',
};

interface AuthTokens {
    accessToken?: string;
    refreshToken?: string;
}

interface UserState {
    user: UserResponse | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    error: string | null;
}

interface UserActions {
    setUser: (user: UserResponse | null) => void;
    setAuthenticated: (isAuthenticated: boolean) => void;
    setLoading: (isLoading: boolean) => void;
    setError: (error: string | null) => void;
    login: (user: UserResponse, tokens?: AuthTokens) => void;
    logout: () => void;
    clearError: () => void;
    setTokens: (tokens: AuthTokens) => void;
    clearTokens: () => void;
    getAccessToken: () => string | null;
}

type UserStore = UserState & UserActions;

const initialState: UserState = {
    user: null,
    isAuthenticated: false,
    isLoading: false,
    error: null,
};

const getJwtPayload = (token: string): { exp?: number } | null => {
    try {
        const body = token.split('.')[1];
        if (!body) return null;

        const normalized = body
            .replace(/-/g, '+')
            .replace(/_/g, '/')
            .padEnd(body.length + (4 - (body.length % 4 || 4)) % 4, '=');

        const decoded = atob(normalized);
        return JSON.parse(decoded) as { exp?: number };
    } catch {
        return null;
    }
};

const isTokenExpired = (token: string): boolean => {
    const payload = getJwtPayload(token);
    const exp = payload?.exp;
    if (typeof exp !== 'number' || !Number.isFinite(exp)) {
        return true;
    }
    return exp * 1000 <= Date.now();
};

const clearAuthStorage = (): void => {
    localStorage.removeItem(TOKEN_KEYS.accessToken);
    localStorage.removeItem(TOKEN_KEYS.refreshToken);
};

export const hasValidAccessToken = (): boolean => {
    const token = localStorage.getItem(TOKEN_KEYS.accessToken);
    if (!token) return false;
    return !isTokenExpired(token);
};

export const useUserStore = create<UserStore>()(
    persist(
        (set) => ({
            ...initialState,

            setUser: (user) => set({ user }),
            setAuthenticated: (isAuthenticated) => set({ isAuthenticated }),
            setLoading: (isLoading) => set({ isLoading }),
            setError: (error) => set({ error }),

            /**
             * Login - save user data and tokens
             */
            login: (user, tokens) => {

                // Save tokens to localStorage if provided
                if (tokens?.accessToken) {
                    localStorage.setItem(TOKEN_KEYS.accessToken, tokens.accessToken);
                }
                if (tokens?.refreshToken) {
                    localStorage.setItem(TOKEN_KEYS.refreshToken, tokens.refreshToken);
                }

                set({
                    user,
                    isAuthenticated: true,
                    isLoading: false,
                    error: null,
                });
            },

            /**
             * Logout - clear all user data and tokens
             */
            logout: () => {
                // Clear tokens from localStorage
                clearAuthStorage();
                localStorage.removeItem("tailody-user-storage");
                localStorage.removeItem("tailody-group-storage");

                set(initialState);
            },

            clearError: () => set({ error: null }),

            /**
             * Save tokens to localStorage
             */
            setTokens: (tokens) => {
                if (tokens.accessToken) {
                    localStorage.setItem(TOKEN_KEYS.accessToken, tokens.accessToken);
                }
                if (tokens.refreshToken) {
                    localStorage.setItem(TOKEN_KEYS.refreshToken, tokens.refreshToken);
                }
            },

            /**
             * Clear all tokens from localStorage
             */
            clearTokens: () => {
                clearAuthStorage();
            },

            /**
             * Get current access token
             */
            getAccessToken: () => {
                return localStorage.getItem(TOKEN_KEYS.accessToken);
            },
        }),
        {
            name: 'tailody-user-storage',
            partialize: (state) => ({
                user: state.user,
                isAuthenticated: state.isAuthenticated,
            }),
            onRehydrateStorage: () => (state) => {
                const tokenValid = hasValidAccessToken();

                if (!state) return;
                if (!tokenValid) {
                    state.user = null;
                    state.isAuthenticated = false;
                    state.isLoading = false;
                    state.error = null;
                    return;
                }
                if (!state.user) {
                    state.isAuthenticated = false;
                }
            },
        }
    )
);

// Selectors
export const selectIsAuthenticated = (state: UserStore) => state.isAuthenticated;
export const selectUser = (state: UserStore) => state.user;

// Token keys export for other modules
export { TOKEN_KEYS };
